import React, {useEffect, useState} from "react";
import {Stack, Box, Paper, CircularProgress} from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate, useLocation} from "react-router-dom";
import useGatePass from "../hooks/useGatePass";
import VehiclePlateNumberListDropdownComponent from "../../VehiclePlugin/common/vehicle-plate-number-list-dropdown.component";
import CustomDatePicker from "../../../core/components/common/custom-datepicker.component";
import dayjs from "dayjs";
import useVehicle from "../../VehiclePlugin/hooks/useVehicle";
import useUserProfile from "../../ProfilePlugin/hooks/useUserProfile";

const EditGatePassComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {gatePassDetailsData, viewGatePass, isUpdatingGatePass, updateGatePass} = useGatePass();
    const {isFetchingActiveVehicleList, activeVehicleList, getActiveVehicleList} = useVehicle();

    const gatePassId = new URLSearchParams(location.search).get("id");

    const [selectedPlateNumber, setSelectedPlateNumber] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const {viewProfile, profileDetails} = useUserProfile();

    const org_uid = profileDetails?.org_uid || null;

    
    useEffect(() => {
        viewProfile();
    }, []);

    useEffect(() => {
        const parameters = {gatePassUID: gatePassId};
            
        viewGatePass(parameters);
    }, [gatePassId]);

    useEffect(() => {
        if (gatePassDetailsData) {
            setSelectedPlateNumber(gatePassDetailsData?.gate_pass?.plate_number);
            setStartDate(dayjs(gatePassDetailsData?.gate_pass?.valid_from));
            setEndDate(dayjs(gatePassDetailsData?.gate_pass?.valid_to));
        }
    }, [gatePassDetailsData])


    const handleVehicleChange = (value) => {
        setSelectedPlateNumber(value);
    }

    const handleBack = () => {
        navigate("/gatepass"); 
    }

    const handleEditGatePass = () => {
        const parameters = {gatepassUid: gatePassDetailsData?.gate_pass.gatepass_uid};
        const payload = {
            plate_number: selectedPlateNumber,
            valid_from: dayjs(new Date(startDate)).format("YYYY-MM-DD"),
            valid_to: dayjs(new Date(endDate)).format("YYYY-MM-DD"),
            orgUID: org_uid
        };

        updateGatePass(parameters, payload);
    }


    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Gate Pass List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{m: "auto", maxWidth: "1000px"}}>  
                <Box sx={{textAlign: "start", mt: 2}}>
                    <CustomTypography 
                        variant="h3"
                        text={"Edit Gate Pass"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />

                    <CustomTypography 
                        variant="subtitle1"
                        text={"Update gate pass for entry"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />
                </Box>

                <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px 40px", mt: 3}}>
                    <Box sx={{textAlign: "start", mb: 5}}>
                        <CustomTypography 
                            variant="h3"
                            text={"Gate Pass Details"}
                            customStyles={{
                                marginBottom: 0, 
                                fontSize: 22,
                                fontWeight: 600
                            }}
                        />

                        <CustomTypography 
                            variant="caption"
                            text={"Fill in the details to update gate pass information"}
                            customStyles={{
                                marginBottom: 0,
                                fontSize: 14 
                            }}
                        />
                    </Box>

                    <Stack spacing={2} flex={1}>
                        <Box>
                            <CustomTypography 
                                variant="subtitle2"
                                text={"Plate Number"}
                                customStyles={{
                                    marginBottom: (selectedPlateNumber) ? 1.5 : 1, 
                                }}
                            />

                            <VehiclePlateNumberListDropdownComponent 
                                value={selectedPlateNumber}
                                onChange={handleVehicleChange}
                                fullWidth={true}
                                size={"medium"}
                                disabled={isUpdatingGatePass}
                                isFetchingActiveVehicleList={isFetchingActiveVehicleList}
                                activeVehicleList={activeVehicleList}
                                getActiveVehicleList={getActiveVehicleList}
                                org_uid={org_uid}
                            />
                        </Box>
                    </Stack>

                    <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 4}}>
                        <Box flex={1}>
                            <CustomTypography 
                                variant="subtitle2"
                                text="Start Date"
                                customStyles={{ marginBottom: 1 }}
                            />
                            <CustomDatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newDate) => setStartDate(newDate)}
                                disabled={isUpdatingGatePass}
                                fullWidth
                            />
                        </Box>

                        <Box flex={1}>
                            <CustomTypography 
                                variant="subtitle2"
                                text="End Date"
                                customStyles={{ marginBottom: 1 }}
                            />
                            <CustomDatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newDate) => setEndDate(newDate)}
                                disabled={isUpdatingGatePass}
                                fullWidth
                            />
                        </Box>
                    </Stack>

                    <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 7, justifyContent: "end"}}>
                        <CustomButton 
                            variant={"outlined"}
                            label={"Cancel"}
                            customStyles={{}}
                            onClick={(isUpdatingGatePass) ? null : handleBack}
                        />     

                        <CustomButton 
                            variant={"contained"}
                            label={"Update Gate Pass"}
                            startIcon={isUpdatingGatePass && <CircularProgress size="15px" color="#fff" sx={{mr: "2px"}} />}
                            customStyles={{
                                opacity: (selectedPlateNumber && startDate && endDate && 
                                    (
                                        selectedPlateNumber !== gatePassDetailsData?.gate_pass?.plate_number ||
                                        dayjs(new Date(startDate)).format("YYYY-MM-DD") !== dayjs(gatePassDetailsData?.gate_pass.valid_from).format("YYYY-MM-DD") ||
                                        dayjs(new Date(endDate)).format("YYYY-MM-DD") !== dayjs(gatePassDetailsData?.gate_pass.valid_to).format("YYYY-MM-DD")
                                    ) && !isUpdatingGatePass) ? "100%" : "55%",
                                cursor: (selectedPlateNumber && startDate && endDate && 
                                    (
                                        selectedPlateNumber !== gatePassDetailsData?.gate_pass?.plate_number ||
                                        dayjs(new Date(startDate)).format("YYYY-MM-DD") !== dayjs(gatePassDetailsData?.gate_pass.valid_from).format("YYYY-MM-DD") ||
                                        dayjs(new Date(endDate)).format("YYYY-MM-DD") !== dayjs(gatePassDetailsData?.gate_pass.valid_to).format("YYYY-MM-DD")
                                    ) && !isUpdatingGatePass) ? "pointer" : "not-allowed !important",
                            }}
                            onClick={(selectedPlateNumber && startDate && endDate && 
                                (
                                    selectedPlateNumber !== gatePassDetailsData?.gate_pass?.plate_number ||
                                    dayjs(new Date(startDate)).format("YYYY-MM-DD") !== dayjs(gatePassDetailsData?.gate_pass.valid_from).format("YYYY-MM-DD") ||
                                    dayjs(new Date(endDate)).format("YYYY-MM-DD") !== dayjs(gatePassDetailsData?.gate_pass.valid_to).format("YYYY-MM-DD")
                                ) && !isUpdatingGatePass) ? handleEditGatePass : null}
                        />    
                    </Stack>  
                </Paper>
            </Box>
       </>
    )
}

export default EditGatePassComponent